import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { TFsmEvent } from '@/modules/onboarding/fsm-types';
import { fsmSlice } from '@/modules/onboarding/store/fsm-slice';
import { getPartner } from '@/modules/partner/store/selectors';
import type { IFormProps } from '@/pages/Wizard/view-types';
import { eventTracking } from '@/services/event-tracking';
import { useLicensingAuthorityTypes } from '@/shared/hooks/useLicensingAuthorityTypes';
import { useForm } from '@/shared/view/Form';

import { Form, FormShape, Item as LicensingAuthorityItem } from './Form';

const useOptions = () => {
  const intl = useIntl();
  const licensingAuthorityTypes: LicensingAuthorityItem[] = useLicensingAuthorityTypes();

  const options = useMemo(() => {
    licensingAuthorityTypes.push({
      title: intl.formatMessage({
        defaultMessage: "I don't have license yet",
        id: '22CJ/D',
        description: 'Licensing authority option: None',
      }),
      value: 'None',
    });

    return licensingAuthorityTypes;
  }, [intl, licensingAuthorityTypes]);

  return options;
};

const LicensingAuthority = ({ onSwitchScreen }: IFormProps<TFsmEvent>) => {
  const options = useOptions();
  const partner = useSelector(getPartner);
  const dispatch = useDispatch();

  const postForm = useCallback(
    async (formValues: FormShape) => {
      if (formValues.licensingAuthority !== 'None') {
        dispatch(
          fsmSlice.actions.setDraft({
            licensingAuthority: formValues.licensingAuthority,
          }),
        );
      }

      onSwitchScreen('NEXT', {
        state: formValues.licensingAuthority === 'None' ? 'failed' : '',
      });

      eventTracking.track({
        event:
          formValues.licensingAuthority === 'None'
            ? 'Onboarding - License - Failed'
            : 'Onboarding - License - Success',
        type: formValues.licensingAuthority,
      });

      return {};
    },
    [onSwitchScreen, dispatch],
  );

  const {
    handleSubmit,
    form: { control, formState },
  } = useForm<FormShape>({
    preSaveStore: {
      storeId: `licensingAuthorityStepForm-${partner.id}`,
      type: 'memory',
    },
    onSubmit: postForm,
  });

  return (
    <Form
      items={options}
      control={control}
      onSubmit={handleSubmit}
      isSubmitting={formState.isSubmitting}
      isValid={formState.isValid}
    />
  );
};

export { LicensingAuthority };
